import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  ArrowLeft, ExternalLink, Package, Tag, Ruler, Box,
  ClipboardList, FileText, ShoppingCart, TrendingUp, Wrench,
  ChevronDown, ChevronRight, Plus, Trash2
} from "lucide-react";
import { toast } from 'sonner';
import { API } from '@/config';

const defaultComponent = () => ({ component_id: '', component_name: '', quantity: 1, unit: 'LBS', item_type: 'InvtPart' });

// BOM Version Row Component with Expand/Collapse
const BOMVersionRow = ({ bom, bomIdx }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="px-3 py-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </td>
        <td className="px-3 py-3">
          <span className="font-semibold text-gray-900">Version {bom.version_number}</span>
        </td>
        <td className="px-3 py-3 text-sm text-gray-600">
          {bom.effective_date || 'N/A'}
        </td>
        <td className="px-3 py-3 text-sm text-gray-600">
          {bom.memo || '-'}
        </td>
        <td className="px-3 py-3 text-center text-sm text-gray-900">
          {(bom.components || []).length}
        </td>
        <td className="px-3 py-3 text-center">
          {bom.is_active ? (
            <Badge className="bg-green-100 text-green-700">Active</Badge>
          ) : (
            <Badge variant="outline">Inactive</Badge>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan="6" className="px-3 py-3 bg-gray-50">
            <div className="ml-8">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Components:</h5>
              <table className="w-full">
                <thead className="bg-white border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Component</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(bom.components || []).map((component, idx) => (
                    <tr key={idx} className="hover:bg-gray-100">
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {component.component_name || component.material}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">
                        {component.quantity}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {component.unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default function ItemDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [boms, setBoms] = useState([]);
  const [relatedPOs, setRelatedPOs] = useState([]);
  const [relatedSOs, setRelatedSOs] = useState([]);
  const [relatedWOs, setRelatedWOs] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inventorySortConfig, setInventorySortConfig] = useState({ key: null, direction: 'asc' });
  const [showCreateBOM, setShowCreateBOM] = useState(false);
  const [bomFormComponents, setBomFormComponents] = useState([defaultComponent()]);
  const [bomSaving, setBomSaving] = useState(false);

  useEffect(() => {
    fetchItemDetails();
  }, [itemId]);

  // Create BOM in Rudder is on hold until BOM push/sync to NetSuite can be fully tested.
  const createBOMDisabled = true;

  const openCreateBOM = () => {
    if (createBOMDisabled) return;
    setBomFormComponents(boms.length > 0 && boms[0].components?.length > 0
      ? boms[0].components.map(c => ({
          component_id: c.component_id || '',
          component_name: c.component_name || c.material || '',
          quantity: typeof c.quantity === 'number' ? c.quantity : parseFloat(c.quantity) || 1,
          unit: c.unit || 'LBS',
          item_type: c.item_type || 'InvtPart'
        }))
      : [defaultComponent()]);
    setShowCreateBOM(true);
  };

  const addBOMRow = () => setBomFormComponents(prev => [...prev, defaultComponent()]);
  const removeBOMRow = (index) => setBomFormComponents(prev => prev.filter((_, i) => i !== index));
  const updateBOMRow = (index, field, value) => {
    setBomFormComponents(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleSaveBOM = async () => {
    if (!item || !itemId) return;
    const components = bomFormComponents
      .filter(c => (c.component_id || '').trim() && (c.component_name || '').trim())
      .map(c => ({
        component_id: String(c.component_id).trim(),
        component_name: String(c.component_name).trim(),
        quantity: parseFloat(c.quantity) || 0,
        unit: (c.unit || 'LBS').trim(),
        item_type: (c.item_type || 'InvtPart').trim()
      }));
    if (components.length === 0) {
      toast.error('Add at least one component with ID and name.');
      return;
    }
    setBomSaving(true);
    try {
      await axios.post(`${API}/api/boms`, {
        item_id: String(itemId),
        item_name: item.name || item.sku || itemId,
        components
      });
      toast.success('BOM saved. Work orders for this item will get these components; use "Refresh BOM from item" on existing work orders.');
      setShowCreateBOM(false);
      const bomRes = await axios.get(`${API}/api/items/${itemId}/boms`);
      setBoms(bomRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save BOM');
    } finally {
      setBomSaving(false);
    }
  };

  const fetchItemDetails = async () => {
    try {
      // Fetch item
      const itemRes = await axios.get(`${API}/items/${itemId}`);
      setItem(itemRes.data);

      // Fetch BOMs
      const bomRes = await axios.get(`${API}/items/${itemId}/boms`);
      setBoms(bomRes.data);

      // Fetch related POs (contains this item)
      const posRes = await axios.get(`${API}/warehouse/purchase-orders`);
      const filteredPOs = posRes.data.filter(po => 
        po.line_items.some(li => li.item_id === itemId || li.item_name.includes(itemRes.data.name))
      );
      setRelatedPOs(filteredPOs);

      // Fetch related SOs (contains this item)
      const sosRes = await axios.get(`${API}/warehouse/sales-orders`);
      const filteredSOs = sosRes.data.filter(so => 
        so.line_items.some(li => li.item_id === itemId || li.item_name.includes(itemRes.data.name))
      );
      setRelatedSOs(filteredSOs);

      // Fetch related Work Orders (produces this item)
      const wosRes = await axios.get(`${API}/work-orders`);
      const filteredWOs = wosRes.data.filter(wo => 
        wo.item_name.includes(itemRes.data.name) || wo.item_sku === itemRes.data.sku
      );
      setRelatedWOs(filteredWOs);

      // Fetch current inventory from NetSuite
      const inventoryRes = await axios.get(`${API}/items/${itemId}/inventory`);
      setInventory(inventoryRes.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching item details:', error);
      toast.error('Failed to load item details');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemDetails();
  }, [itemId]);

  const openNetSuite = () => {
    if (item?.netsuite_id) {
      window.open(`https://system.netsuite.com/app/common/item/item.nl?id=${item.netsuite_id}`, '_blank');
      toast.success('Opening NetSuite record...');
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'Finished Goods': 'bg-green-100 text-green-700',
      'Raw Materials': 'bg-blue-100 text-blue-700',
      'Packaging': 'bg-purple-100 text-purple-700',
      'Ingredients': 'bg-orange-100 text-orange-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const handleInventorySort = (key) => {
    let direction = 'asc';
    if (inventorySortConfig.key === key && inventorySortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setInventorySortConfig({ key, direction });
  };

  const getSortedInventory = (zones) => {
    if (!inventorySortConfig.key) return zones;

    return zones.map(zone => ({
      ...zone,
      locations: zone.locations.map(location => {
        const sortedLots = [...location.lots].sort((a, b) => {
          let aVal, bVal;
          
          switch (inventorySortConfig.key) {
            case 'location':
              aVal = location.location_code;
              bVal = location.location_code;
              break;
            case 'lot':
              aVal = a.lot_number;
              bVal = b.lot_number;
              break;
            case 'expiration':
              aVal = a.earliest_expiration || '';
              bVal = b.earliest_expiration || '';
              break;
            case 'quantity':
              aVal = a.quantity;
              bVal = b.quantity;
              break;
            default:
              return 0;
          }

          if (aVal < bVal) return inventorySortConfig.direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return inventorySortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        });

        return { ...location, lots: sortedLots };
      })
    }));
  };

  const SortableHeader = ({ label, sortKey }) => (
    <th 
      className="px-4 py-2 text-left font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => handleInventorySort(sortKey)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {inventorySortConfig.key === sortKey && (
          <span className="text-blue-600">
            {inventorySortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Item not found</p>
          <Button onClick={() => navigate('/items')} className="mt-4">
            Back to Items
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/items')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{item.name}</h1>
            <p className="text-gray-600">{item.sku}</p>
          </div>
        </div>
        <Button variant="outline" onClick={openNetSuite} className="gap-2">
          <ExternalLink className="w-4 h-4" />
          Open in NetSuite
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - 2/3 width */}
        <div className="col-span-2 space-y-6">
          {/* Item Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Item Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    SKU
                  </label>
                  <p className="font-medium text-gray-900 mt-1">{item.sku}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Product Group</label>
                  <p className="font-medium text-gray-900 mt-1">{item.category || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Item Type</label>
                  <p className="font-medium text-gray-900 mt-1">{item.item_type || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1">
                    <Ruler className="w-4 h-4" />
                    Unit of Measure
                  </label>
                  <p className="font-medium text-gray-900 mt-1">{item.unit}</p>
                </div>
              </div>
              {item.description && (
                <div>
                  <label className="text-sm text-gray-500">Description</label>
                  <p className="text-gray-900 mt-1">{item.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Inventory from NetSuite */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Box className="w-5 h-5" />
                  Current Inventory
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  from NetSuite
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {inventory ? (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div>
                      <label className="text-xs text-blue-600 font-medium uppercase">Total Quantity</label>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {inventory.total_quantity.toLocaleString()} {item.unit}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-blue-600 font-medium uppercase">Total Pallets</label>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {inventory.total_pallets}
                      </p>
                    </div>
                  </div>

                  {/* Inventory Summary by Location and Lot */}
                  {inventory.by_facility && inventory.by_facility.length > 0 ? (
                    <div className="space-y-4">
                      {inventory.by_facility.map((facility, facilityIdx) => (
                        <div key={facilityIdx} className="space-y-3">
                          {/* Zones within Facility */}
                          {getSortedInventory(facility.zones).map((zone, zoneIdx) => (
                            <div key={zoneIdx}>
                              {/* Zone Header */}
                              <div className="bg-gray-100 px-4 py-2 border-b font-semibold text-gray-900 mb-2">
                                {zone.zone_display}
                              </div>

                              {/* Table of Locations and Lots */}
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-gray-50 border-b text-xs">
                                    <tr>
                                      <SortableHeader label="Location" sortKey="location" />
                                      <SortableHeader label="Lot Number" sortKey="lot" />
                                      <SortableHeader label="Expiration" sortKey="expiration" />
                                      <th 
                                        className="px-4 py-2 text-right font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleInventorySort('quantity')}
                                      >
                                        <div className="flex items-center justify-end gap-1">
                                          <span>Quantity</span>
                                          {inventorySortConfig.key === 'quantity' && (
                                            <span className="text-blue-600">
                                              {inventorySortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                          )}
                                        </div>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {zone.locations.map((location, locIdx) => (
                                      location.lots.map((lot, lotIdx) => (
                                        <tr key={`${locIdx}-${lotIdx}`} className="hover:bg-gray-50">
                                          <td className="px-4 py-3">
                                            <span className="font-mono text-sm text-gray-900">{location.location_code}</span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="font-mono text-sm text-gray-900">{lot.lot_number}</span>
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-600">
                                            {lot.earliest_expiration ? new Date(lot.earliest_expiration).toLocaleDateString() : '-'}
                                          </td>
                                          <td className="px-4 py-3 text-right">
                                            <span className="font-semibold text-gray-900">{lot.quantity.toLocaleString()}</span>
                                            <span className="text-sm text-gray-500 ml-1">{lot.uom}</span>
                                          </td>
                                        </tr>
                                      ))
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Box className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No inventory found for this item</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading inventory...</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bill of Materials - Tabular with Expand */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Bill of Materials ({boms.length} {boms.length === 1 ? 'Version' : 'Versions'})
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={createBOMDisabled ? 'inline-block' : undefined}>
                          <Button size="sm" variant="default" onClick={openCreateBOM} disabled={createBOMDisabled}>
                            <Plus className="w-4 h-4 mr-1" />
                            Create / Update BOM
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {createBOMDisabled && (
                        <TooltipContent>
                          On hold until NetSuite BOM sync is testable
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  {boms.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      from NetSuite
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {boms.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Effective Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Components</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {boms.map((bom, bomIdx) => (
                        <BOMVersionRow key={bomIdx} bom={bom} bomIdx={bomIdx} />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No BOM versions defined</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={createBOMDisabled ? 'inline-block mt-3' : undefined}>
                          <Button size="sm" className="mt-3" onClick={openCreateBOM} disabled={createBOMDisabled}>
                            <Plus className="w-4 h-4 mr-1" />
                            Create BOM
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {createBOMDisabled && (
                        <TooltipContent>
                          On hold until NetSuite BOM sync is testable
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create / Update BOM Dialog */}
          <Dialog open={showCreateBOM} onOpenChange={setShowCreateBOM}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create / Update BOM for this item</DialogTitle>
              </DialogHeader>
              {item && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <Label className="text-gray-500">Item ID</Label>
                      <p className="font-mono">{itemId}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Item name</Label>
                      <p>{item.name || item.sku || '—'}</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Components (per unit of finished good)</Label>
                      <Button type="button" size="sm" variant="outline" onClick={addBOMRow}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add row
                      </Button>
                    </div>
                    <div className="border rounded-md overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="text-left px-2 py-2 font-medium">Component ID</th>
                            <th className="text-left px-2 py-2 font-medium">Component name</th>
                            <th className="text-right px-2 py-2 font-medium w-24">Quantity</th>
                            <th className="text-left px-2 py-2 font-medium w-28">Unit</th>
                            <th className="text-left px-2 py-2 font-medium w-28">Type</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bomFormComponents.map((row, index) => (
                            <tr key={index}>
                              <td className="px-2 py-1">
                                <Input
                                  value={row.component_id}
                                  onChange={(e) => updateBOMRow(index, 'component_id', e.target.value)}
                                  placeholder="e.g. 1384"
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input
                                  value={row.component_name}
                                  onChange={(e) => updateBOMRow(index, 'component_name', e.target.value)}
                                  placeholder="e.g. Tortilla"
                                  className="h-8 text-xs"
                                />
                              </td>
                              <td className="px-2 py-1 text-right">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={row.quantity}
                                  onChange={(e) => updateBOMRow(index, 'quantity', e.target.value)}
                                  className="h-8 text-xs text-right"
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Select
                                  value={row.unit || 'LBS'}
                                  onValueChange={(v) => updateBOMRow(index, 'unit', v)}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="LBS">LBS</SelectItem>
                                    <SelectItem value="Eaches">Eaches</SelectItem>
                                    <SelectItem value="units">units</SelectItem>
                                    <SelectItem value="Roll Film">Roll Film</SelectItem>
                                    <SelectItem value="g">g</SelectItem>
                                    <SelectItem value="kg">kg</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1">
                                <Select
                                  value={row.item_type || 'InvtPart'}
                                  onValueChange={(v) => updateBOMRow(index, 'item_type', v)}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="InvtPart">InvtPart</SelectItem>
                                    <SelectItem value="NonInvtPart">NonInvtPart</SelectItem>
                                    <SelectItem value="food">food</SelectItem>
                                    <SelectItem value="packaging">packaging</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-red-600"
                                  onClick={() => removeBOMRow(index)}
                                  disabled={bomFormComponents.length <= 1}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowCreateBOM(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveBOM} disabled={bomSaving}>
                      {bomSaving ? 'Saving...' : 'Save BOM'}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">

          {/* Related Purchase Orders - Only for Raw Materials */}
          {(item.category === 'Raw Materials' || item.category === 'Ingredients' || item.category === 'Packaging') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Related Purchase Orders ({relatedPOs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {relatedPOs.length > 0 ? (
                <div className="space-y-2">
                  {relatedPOs.slice(0, 5).map((po) => (
                    <div 
                      key={po.po_id}
                      onClick={() => navigate(`/purchase-orders/${po.po_id}`)}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <div>
                        <div className="font-semibold text-sm">{po.po_number}</div>
                        <div className="text-xs text-gray-600">{po.vendor} • {po.expected_delivery_date}</div>
                      </div>
                      <Badge className={po.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                        {po.status}
                      </Badge>
                    </div>
                  ))}
                  {relatedPOs.length > 5 && (
                    <Button variant="outline" className="w-full" onClick={() => navigate('/purchase-orders')}>
                      View all {relatedPOs.length} POs
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No purchase orders found</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

          {/* Related Sales Orders - Only for Finished Goods (brand names) */}
          {(item.category !== 'Raw Materials' && item.category !== 'Ingredients' && item.category !== 'Packaging') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Related Sales Orders ({relatedSOs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {relatedSOs.length > 0 ? (
                <div className="space-y-2">
                  {relatedSOs.slice(0, 5).map((so) => (
                    <div 
                      key={so.so_id}
                      onClick={() => navigate(`/sales-orders/${so.so_id}`)}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <div>
                        <div className="font-semibold text-sm">{so.so_number}</div>
                        <div className="text-xs text-gray-600">{so.customer} • {so.requested_ship_date}</div>
                      </div>
                      <Badge className={so.status === 'shipped' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                        {so.status}
                      </Badge>
                    </div>
                  ))}
                  {relatedSOs.length > 5 && (
                    <Button variant="outline" className="w-full" onClick={() => navigate('/sales-orders')}>
                      View all {relatedSOs.length} SOs
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No sales orders found</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

          {/* Related Work Orders - Only for Finished Goods (brand names) */}
          {(item.category !== 'Raw Materials' && item.category !== 'Ingredients' && item.category !== 'Packaging') && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Related Work Orders ({relatedWOs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {relatedWOs.length > 0 ? (
                <div className="space-y-2">
                  {relatedWOs.slice(0, 5).map((wo) => (
                    <div 
                      key={wo.wo_id}
                      onClick={() => navigate(`/work-order/${wo.wo_id}`)}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <div>
                        <div className="font-semibold text-sm">{wo.wo_number}</div>
                        <div className="text-xs text-gray-600">{wo.production_line} • {wo.planned_date}</div>
                      </div>
                      <Badge className={wo.status === 'in_production' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>
                        {wo.status}
                      </Badge>
                    </div>
                  ))}
                  {relatedWOs.length > 5 && (
                    <Button variant="outline" className="w-full" onClick={() => navigate('/dashboard')}>
                      View all {relatedWOs.length} Work Orders
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No work orders found</p>
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
}
